import Modal from './Modal'
import Button from '../Buttons'
import { useState } from 'react'
import { LuCheck, LuShield, LuScanText, LuFileText, LuListOrdered } from 'react-icons/lu'
import useCheckout from '../../hooks/checkout'
import { useAnnualPlan } from '../../hooks/featureFlags'
import { usePostHog } from 'posthog-js/react'
import { useAuth } from '../../contexts/Auth'
import useTrialEssayCredits from '../../hooks/useTrialEssayCredits'
import { useSelector } from 'react-redux'
import { selectUser } from '../../redux/systemSlice'
import { Elements, useElements, useStripe } from '@stripe/react-stripe-js'
import { atom, useAtom } from 'jotai'
import { loadStripe } from '@stripe/stripe-js'
import StripePaymentMethodSelector from './UpdatePaymentMethod'

const features = [
	{
		icon: LuFileText,
		title: 'Unlimited Essays',
		description: 'Generate as many essays as you need, no limits',
	},
	{
		icon: LuShield,
		title: 'Unlimited Humanization',
		description: 'Bypass all major AI detection tools',
	},
	{
		icon: LuScanText,
		title: 'Unlimited AI Detection',
		description: 'Scan for AI-generated text with 98% accuracy',
	},
	{
		icon: LuListOrdered,
		title: 'Unlimited Essay Outlines',
		description: 'Generate detailed outlines for any topic',
	},
]

const goPremiumStateAtom = atom({
	showSuccessModal: false,
	checkingPayment: false,
	loadingUpdatePayment: false,
	failedPaymentModalOpen: false,
	failedPaymentReason: '',
	termsAccepted: false,
})

function TrialUpgrade({
	open,
	setModalOpen,
	openForPaymentRetries,
	outOfCredits,
	openForUndetectable,
	setClientSecret,
}: {
	open: boolean
	setModalOpen: (open: boolean) => void
	openForPaymentRetries?: boolean
	outOfCredits?: boolean
	openForUndetectable?: boolean
	setClientSecret: (clientSecret: string) => void
}) {
	const [goPremiumState, setGoPremiumState] = useAtom(goPremiumStateAtom)
	const [annualPlan] = useAnnualPlan()
	const { currentUser } = useAuth() as any
	const { hasCredits, essayLimit } = useTrialEssayCredits()
	const user = useSelector(selectUser)
	const stripe = useStripe()
	const elements = useElements()

	const upgradeFreeTrial = async () => {
		setGoPremiumState({
			...goPremiumState,
			checkingPayment: true,
		})
		const requestOptions = {
			method: 'POST',
			headers: {
				'Content-Type': 'application/json',
				authorization: currentUser ? `Bearer ${await currentUser.getIdToken()}` : '',
			},
			body: JSON.stringify({
				user_id: user.id,
			}),
		}
		fetch(process.env.REACT_APP_API_ROOT + '/payments/upgrade_free_trial/', requestOptions)
			.then((res) => res.json())
			.then(async (res) => {
				if (res.requiresConfirmation && res.clientSecret && stripe && elements) {
					const { error: confirmError } = await stripe?.confirmPayment({
						clientSecret: res.clientSecret,
						confirmParams: {
							return_url: window.location.origin + `/?showModal=true${annualPlan ? '&annual=true' : ''}`,
						},
					})
					if (confirmError?.decline_code) {
						setClientSecret(res.clientSecret)
						setTimeout(() => {
							setModalOpen(false)
						}, 300)
						setGoPremiumState({
							...goPremiumState,
							checkingPayment: false,
						})
					} else {
						window.location.href = window.location.origin + `/choose/?showModal=true${annualPlan ? '&annual=true' : ''}`
						return
					}
					return
				} else if (res.error) {
					setClientSecret(res.clientSecret)
				} else {
					window.location.reload()
					setGoPremiumState({
						...goPremiumState,
						showSuccessModal: true,
						checkingPayment: false,
					})
				}
			})
	}

	return (
		<Modal open={open} closeModal={() => setModalOpen(false)} portalClassName="trial-upgrade">
			<div className="px-6 pt-8 pb-6 max-w-[440px]">
				<h2 className="text-2xl font-bold text-center mb-2">Unlock Unlimited Access</h2>
				{!hasCredits && (
					<p className="text-sm text-gray-700 text-center mb-4">
						You've reached the {essayLimit} essay limit for your trial, but it doesn't have to end here!
					</p>
				)}
				<div className="flex flex-col gap-3">
					{features.map((feature) => (
						<div key={feature.title} className="flex items-center gap-4 border border-gray-200 rounded-xl px-4 py-3.5">
							<div className="flex items-center justify-center w-9 h-9 bg-gray-100 rounded-lg shrink-0">
								{feature.icon({ size: 18, className: 'text-gray-600' })}
							</div>
							<div className="flex flex-col grow">
								<div className="font-semibold text-sm">
									<span className="font-bold">Unlimited</span> {feature.title.replace('Unlimited ', '')}
								</div>
								<div className="text-xs text-gray-500">{feature.description}</div>
							</div>
							{LuCheck({ size: 20, className: 'text-emerald-500 shrink-0' })}
						</div>
					))}
				</div>

				<label className="flex items-center justify-center gap-2.5 mt-6 cursor-pointer select-none">
					<input
						type="checkbox"
						checked={goPremiumState.termsAccepted}
						onChange={(e) => setGoPremiumState({ ...goPremiumState, termsAccepted: e.target.checked })}
						className="mt-0.5 w-4 h-4 accent-link cursor-pointer"
					/>
					<span className="text-sm text-gray-600">
						I accept the{' '}
						<a
							href="/post/terms-of-service"
							target="_blank"
							rel="noopener noreferrer"
							className="underline text-gray-800 hover:text-black"
						>
							Terms of Service
						</a>{' '}
						and agree to be billed.
					</span>
				</label>

				<Button
					type="default"
					disabled={!goPremiumState.termsAccepted}
					onClick={upgradeFreeTrial}
					className="w-full !bg-indigo-600 hover:!bg-indigo-700 text-white font-bold py-3 rounded-xl text-base mt-4"
				>
					{goPremiumState.checkingPayment ? 'Loading...' : 'Get Started'}
				</Button>

				<div className="text-center mt-3">
					<button
						onClick={() => setModalOpen(false)}
						className="text-sm text-indigo-600 hover:text-indigo-800 font-medium cursor-pointer bg-transparent border-none"
					>
						Maybe Later
					</button>
				</div>
			</div>
		</Modal>
	)
}

const stripePromise = loadStripe(
	'pk_live_51KXx04ApflHFLqu66s7RJuXDpQMqxcP4Hr3YmgRCFPP4Ylm7sNmWiP5mVddhxi2iqdSkvWkheM3zd6nJ6HhOrBaf00l05v3pXq'
)

const StripeGoPremium = ({
	open,
	setModalOpen,
	openForPaymentRetries,
	outOfCredits = true,
	openForUndetectable = false,
}: {
	open: boolean
	setModalOpen: (open: boolean) => void
	openForPaymentRetries?: boolean
	outOfCredits?: boolean
	openForUndetectable?: boolean
}) => {
	const [clientSecret, setClientSecret] = useState('')
	return (
		<Elements stripe={stripePromise}>
			<TrialUpgrade
				open={open}
				setModalOpen={setModalOpen}
				openForPaymentRetries={openForPaymentRetries}
				outOfCredits={outOfCredits}
				openForUndetectable={openForUndetectable}
				setClientSecret={setClientSecret}
			/>
			<StripePaymentMethodSelector clientSecret={clientSecret} />
		</Elements>
	)
}

export default StripeGoPremium
